import { createStackNavigator } from '@react-navigation/stack';
import { Drawer } from 'react-native-drawer-layout';
import {
    Logout,
    ErrorPage,
    Login,
    Details,
    ResetPin,
    RecordPage,
    PasswordManagement,
    Filter,
    CopyFavorites,
    Onboarding,
    QuickOnboardingInfoScene,
    SuspendErrorScene,
    PreConsentScene,
    VoyoPromoScene,
    PackageInfoScene,
    ConsentsIntroPage,
    ForgottenUsername
} from 'Pages';
import Theme from 'Theme';
import React, { useEffect, useState, useCallback } from 'react';
import { Device } from 'DeviceUtils';
import SimpleNotificationProvider from 'Pages/SimpleNotification/SimpleNotificationProvider';
import { useAppSelector, useAppDispatch } from 'ReduxStore/hooks';
import { getFocusedRouteNameFromRoute, useNavigation } from '@react-navigation/native';
import { useSettingsNavigation } from 'Pages/Settings/components/SettingsNavigationContext/SettingsNavigationContext';
import { setActiveMenuItem } from 'ReduxStore/webMenu';
import { show, hide } from 'ReduxStore/drawer';
import { useStore } from 'Store/Store';
import NavigationService from './navigationService';
import Menu from './Menu/menu';
import { drawerOptions, navigationStyles, topMenuOptions } from './navigationOptions';
import CreateRootStack from './createRootStack';
import { useNavigationPropsContext } from './navigationPropsContext';

const screenPathExtractor = (screensObj) => {
    if (!screensObj) return;
    if (screensObj === undefined || screensObj === null) return;

    const screenPaths = Object.keys(screensObj).map((key) => {
        if (key === 'Main') {
            return screensObj[key].screens;
        }
        if (screensObj[key].path !== undefined) {
            return { key, path: screensObj[key].path };
        }
        return screenPathExtractor(screensObj[key].screens);
    });
    return screenPaths;
};

const Nav = createStackNavigator();
const Stack = createStackNavigator();
const Wrapper = ({ children, items, navigation, isLogInOut }) => {
    const [hideHeader, setHideHeader] = useState(true);
    const open = useAppSelector((state) => state.drawer.open);
    const dispatch = useAppDispatch();
    useEffect(() => {
        setHideHeader(isLogInOut);
    }, [isLogInOut]);
    return Device.isWeb ? (
        <>
            {!hideHeader && <Menu items={items} navigation={navigation} />}
            {children}
        </>
    ) : (
        <Drawer
            open={open}
            onOpen={() => {
                dispatch(show());
            }}
            onClose={() => {
                dispatch(hide());
            }}
            drawerStyle={drawerOptions.drawerStyle}
            renderDrawerContent={(drawerProps) => (
                <Menu items={items} {...drawerProps} navigation={navigation} />
            )}
        >
            {children}
        </Drawer>
    );
};

const NavScreen = (props) => {
    const {
        navigatorRoutes,
        initialRouteName,
        menusItems,
        pathOptions,
        insertedPath,
        insertedInitRoute,
        route,
        userData
    } = props;

    const drawerNavigatorRoutesArray = [];
    Object.keys(navigatorRoutes).forEach((key) => {
        if (navigatorRoutes[key].requiresLogin && !userData) {
            return;
        }
        drawerNavigatorRoutesArray.push(
            <Nav.Screen
                name={key}
                key={`${key}navigation`}
                component={navigatorRoutes[key].rootStack.screen}
                initialParams={navigatorRoutes[key].rootStack.params}
                options={navigatorRoutes[key].rootStack.navigationOptions}
            />
        );
    });

    // drawerNavigatorRoutesArray.push(
    //     <Nav.Screen
    //         name="rootStack"
    //         key="rootStackNavigation"
    //         options={{
    //             headerShown: false,
    //             animationEnabled: !Device.isWeb
    //         }}
    //     >
    //         {() => <CreateRootStack userData={userData} Nav={Nav} />}
    //     </Nav.Screen>
    // );

    const { routeConfigArray, screenOptions } = CreateRootStack({ userData, Nav });

    return Device.isWeb ? (
        <Nav.Navigator
            mode="modal"
            initialRouteName={insertedInitRoute} // main setup of initial location
            swipeEnabled={false}
            backBehavior="history"
            detachInactiveScreens
            screenOptions={{
                lazy: true,
                cardStyle: { flex: 1, overflow: 'auto' },
                autoHideHomeIndicator: true
            }}
        >
            <Nav.Group screenOptions={topMenuOptions}>{drawerNavigatorRoutesArray}</Nav.Group>
            <Nav.Group screenOptions={screenOptions}>{routeConfigArray}</Nav.Group>
        </Nav.Navigator>
    ) : (
        <Nav.Navigator
            mode="modal"
            initialRouteName={initialRouteName}
            swipeEnabled={false}
            screenOptions={{
                lazy: true,
                gestureEnabled: false,
                detachPreviousScreen: true
            }}
            backBehavior="history"
            detachInactiveScreens
        >
            <Nav.Group screenOptions={drawerOptions}>{drawerNavigatorRoutesArray}</Nav.Group>
            <Nav.Group screenOptions={screenOptions}>{routeConfigArray}</Nav.Group>
        </Nav.Navigator>
    );
};

const AppContainer = (props) => {
    const {
        menuItems,
        onboardingCompleted,
        showEnforceChangePassword,
        customOnboardingItems,
        // navigation,
        insertedPath,
        pathOptions,
        onNavigationStateChange
    } = props;

    const navigation = useNavigation();
    const { items, routes } = menuItems;
    const [navigatorRoutes, setNavigatorRoutes] = useState({});
    const [initialRoute, setInitialRoute] = useState(null);
    const { navigate, updateNavigationProps } = useNavigationPropsContext();
    const [insertedInitRoute, setInsertedInitRoute] = useState(null);
    const navigationInstance = NavigationService.getTopLevelNavigationInstance();
    const { initRoute, setInitRoute } = useSettingsNavigation();
    const dispatch = useAppDispatch();
    const activeMenuItem = useAppSelector((state) => state.webMenu.activeMenuItem);
    const { name } = routes;
    const [isLogInOut, setIsLogInOut] = useState(false);
    const { userData } = useStore();
    const routeListener = useCallback(
        ({ route }) => ({
            state: () => {
                const subRoute = getFocusedRouteNameFromRoute(route);
                if (subRoute) {
                    dispatch(setActiveMenuItem(subRoute));
                }
                if (route.name === 'Logout' || route.name === 'Login') {
                    setIsLogInOut(true);
                } else {
                    setIsLogInOut(false);
                }
                // (undefined, '', relativePath);
            }
        }),
        []
    );

    useEffect(() => {
        if (!activeMenuItem) {
            dispatch(setActiveMenuItem(insertedInitRoute));
        }
        const newNavigatorRoutes = {};
        Object.keys(routes).forEach((route) => {
            newNavigatorRoutes[route] = () => <CreateRootStack options={routes[route]} />;
        });
        setNavigatorRoutes(newNavigatorRoutes);
        // Set route globally for menu
        const nav = navigationInstance.getState();
        if (nav?.routes?.length === 1 && nav.routes[0].state?.routes?.length === 1) {
            const activeRouteItem = nav.routes[0].state?.routes[0]?.name ?? 'home';
            dispatch(setActiveMenuItem(activeRouteItem));
        }
    }, []);

    useEffect(() => {
        if (!Device.isWeb) return;
        /** Fetching data based on provided location of route on web, parsing the keys and seting content
         * This effect runs once when the component mounts. It performs the following tasks:
         * 1. Creates a new object `newNavigatorRoutes` where each key corresponds to a route name
         *    and the value is a function that returns a React component (`CreateRootStack`) with the route options.
         * 2. Updates the state `navigatorRoutes` with the newly created `newNavigatorRoutes` object.
         * 3. Retrieves the current navigation state using `navigationInstance.getState()`.
         * 4. Checks if the navigation state has only one route and that route has only one nested route.
         * 5. If the above condition is met, it sets the active menu item in the `NavigationService` to the name of the nested route.
         */
        if (
            insertedPath.mainLocation === '' &&
            insertedPath.secondLayer === '' &&
            insertedPath.asset === ''
        )
            return;
        const isSettings = insertedPath.mainLocation.split('?')[0] === 'settings';
        let location;
        if (insertedPath.mainLocation !== '' || insertedPath.mainLocation !== null) {
            const data = screenPathExtractor(pathOptions);
            if (!data) return;
            if (isSettings) {
                // const mainKey = findDeepestKey(data[0], 'settings');
                setInsertedInitRoute('settings');
                dispatch(setActiveMenuItem('settings'));
                setInitRoute(insertedPath.mainLocation.split('=')[1]);
                return;
            }
            location = insertedPath.mainLocation.split('?')[0];

            const mainKey = findDeepestKey(data[0], location);
            if (mainKey) {
                setInsertedInitRoute(mainKey);
                dispatch(setActiveMenuItem(mainKey));
                // if (mainKey === 'settings') {
                //     setInsertedInitRoute('settings');
                //     setInitRoute(insertedPath.mainLocation.split('=')[1]);
                // }
            }
        }
        if (insertedPath.asset && insertedPath.asset.id) {
            navigate('Details', null, {
                id: insertedPath.asset.id,
                parentId: insertedPath.asset?.parentId,
                type: insertedPath.asset.type
            });
            // id -> handle loading of detail page via id
        }
    }, [insertedPath]);

    /**
     * Recursively searches through an object to find the deepest key that matches a given location.
     * Used to find the key of screen navigation that corresponds to a given location path on web.
     *
     * @param {Object} obj - The object to search through.
     * @param {string} location - The location path to match against.
     * @returns {string|null} - The key that matches the location, or null if no match is found.
     */
    const findDeepestKey = (obj, location) => {
        let foundKey = null;
        Object.keys(obj).forEach((key) => {
            if (obj[key]?.path === location) {
                foundKey = key;
            } else if (obj[key]?.screens) {
                if (insertedPath.secondLayer) {
                    // TODO: check if this is correct, if you can not open links othen than videostore, this was added lately
                    if (key === 'videostore') {
                        const upperKeyObject = obj[key].screens;
                        Object.keys(upperKeyObject).forEach((innerKey) => {
                            const nestedRoutes = upperKeyObject[innerKey].screens;
                            const loacationSecondLayer = insertedPath.secondLayer.split('?')[0];
                            foundKey = loacationSecondLayer;
                            updateNavigationProps({ videoStoreTab: loacationSecondLayer });
                        });
                        const nestedKey = findDeepestKey(obj[key].screens, location);
                        if (nestedKey) {
                            foundKey = nestedKey;
                        }
                    }
                } else {
                    const nestedKey = findDeepestKey(obj[key].screens, location);
                    if (nestedKey) {
                        foundKey = nestedKey;
                    }
                }
            }
        });
        return foundKey;
    };

    let initialRouteName; //= insertedPath.mainLocation.split('?')[0]; // 'home';
    let initialRouteParams = {};
    if (!onboardingCompleted && !Device.isWeb) {
        initialRouteName = 'Onboarding';
        initialRouteParams = { customOnboardingItems };
    }

    if (userData && userData.enforceChangePassword && showEnforceChangePassword) {
        initialRouteName = 'PasswordManagement';
        initialRouteParams = {
            initialRoute: 'changeUsernameQuestion',
            backRoute: 'home'
        };
    }
    const stackConfig = {
        initialRouteName,
        screenOptions: {
            presentation: 'card',
            animation: 'fade',
            gestureEnabled: false,
            headerShown: false,
            headerStyle: navigationStyles.headerStyle,
            headerTintColor: Theme.primaryTextColor,
            headerTitleStyle: navigationStyles.headerTitleStyle,
            cardStyle: { backgroundColor: Theme.primaryBackgroundColor },
            autoHideHomeIndicator: true,
            detachPreviousScreen: true
        }
    };

    if (Object.keys(navigatorRoutes).length === 0) {
        return null;
    }

    const initialMenuRouteName = initialRoute; // userData ? 'watch-tv' : 'home';

    return (
        <Wrapper
            options={drawerOptions}
            items={items}
            navigation={navigation}
            isLogInOut={isLogInOut}
        >
            <Stack.Navigator detachInactiveScreens {...stackConfig}>
                <Stack.Screen
                    name="Main"
                    initialParams={initialRouteName === 'Main' ? initialRouteParams : null}
                    listeners={routeListener}
                    screenOptions={{ lazy: true }}
                >
                    {(props) => (
                        <NavScreen
                            initialRouteName={initialMenuRouteName} // 'home
                            insertedInitRoute={insertedInitRoute}
                            navigatorRoutes={routes}
                            menusItems={items}
                            {...props}
                            insertedPath={insertedPath}
                            pathOptions={pathOptions}
                            userData={userData}
                        />
                    )}
                </Stack.Screen>
                <Stack.Screen name="ErrorPage" component={ErrorPage} listeners={routeListener} />
                <Stack.Screen
                    name="PasswordManagement"
                    component={PasswordManagement}
                    initialParams={
                        initialRouteName === 'PasswordManagement' ? initialRouteParams : null
                    }
                    listeners={routeListener}
                    options={{
                        animation: !Device.isWeb ? 'slide_from_right' : 'none'
                    }}
                />
                <Stack.Screen
                    name="ForgottenUsername"
                    component={ForgottenUsername}
                    listeners={routeListener}
                    options={{
                        animation: !Device.isWeb ? 'slide_from_right' : 'none'
                    }}
                />

                <Stack.Screen
                    name="InfoScene"
                    component={QuickOnboardingInfoScene}
                    listeners={routeListener}
                />

                <Stack.Screen
                    name="Onboarding"
                    component={Onboarding}
                    initialParams={initialRouteName === 'Onboarding' ? initialRouteParams : null}
                    listeners={routeListener}
                />
                {userData && (
                    <>
                        <Stack.Screen
                            name="ResetPin"
                            component={ResetPin}
                            listeners={routeListener}
                        />
                        <Stack.Screen name="Filter" component={Filter} listeners={routeListener} />
                        <Stack.Screen
                            name="RecordPage"
                            component={RecordPage}
                            listeners={routeListener}
                        />
                        <Stack.Screen
                            name="CopyFavorites"
                            component={CopyFavorites}
                            listeners={routeListener}
                        />
                        <Stack.Screen
                            name="SuspendScene"
                            component={SuspendErrorScene}
                            listeners={routeListener}
                        />
                        <Stack.Screen
                            name="PreConsentScene"
                            component={PreConsentScene}
                            listeners={routeListener}
                        />
                        <Stack.Screen
                            name="VoyoPromoScene"
                            component={VoyoPromoScene}
                            listeners={routeListener}
                        />
                        <Stack.Screen
                            name="PackageInfoScene"
                            component={PackageInfoScene}
                            listeners={routeListener}
                        />
                        <Stack.Screen
                            name="ConsentsIntroPage"
                            component={ConsentsIntroPage}
                            listeners={routeListener}
                        />
                        <Stack.Screen
                            name="SimpleNotification"
                            component={SimpleNotificationProvider}
                            listeners={routeListener}
                        />
                    </>
                )}
                <Stack.Screen name="Login" component={Login} listeners={routeListener} />
                <Stack.Screen name="Logout" component={Logout} listeners={routeListener} />
            </Stack.Navigator>
        </Wrapper>
    );
};
export default AppContainer;
